import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { loadTemplatesFromStorage } from '../services/templateService';
import AddonPricingModal from '../components/AddonPricingModal';
import { renderTemplate } from '../utils/templateRenderer';
import { sanitizeTemplateHTML } from '../utils/templateSanitizer';
import { getSampleResumeData } from '../utils/sampleResumeData';
import { extractTemplateTokens } from '../utils/templateFieldDetector';
import { getCachedTemplates, cacheRenderedTemplates } from '../utils/templateCache';
import { customToast } from '../utils/toastUtils';
import TemplateSkeletonCard from '../components/TemplateSkeletonCard';
import LazyTemplateCard from '../components/LazyTemplateCard';
import '../components/TemplateSkeletonCard.css';
import './TemplatesGallery.css';

// Number of templates to display per page
const TEMPLATES_PER_PAGE = 18;

// Batch size for rendering (how many to render in parallel per batch)
const RENDER_BATCH_SIZE = 6;

// Sample names for template previews
const PREVIEW_NAMES = [
  {
    fullName: "John Doe",
    title: "Senior Product Manager",
    email: "john.doe@email.com",
    phone: "+1 (555) 234-5678",
    location: "New York, NY"
  },
  {
    fullName: "Jamie Luis",
    title: "Head of Marketing Strategy",
    email: "jamie.luis@email.com",
    phone: "+1 (555) 345-6789",
    location: "Los Angeles, CA"
  },
  {
    fullName: "Lara Jane",
    title: "Senior Research Scientist",
    email: "lara.jane@email.com",
    phone: "+1 (555) 456-7890",
    location: "Chicago, IL"
  },
  {
    fullName: "Harry Mills",
    title: "Business Development Director",
    email: "harry.mills@email.com",
    phone: "+1 (555) 567-8901",
    location: "Austin, TX"
  },
  {
    fullName: "Clara Vincy",
    title: "UX/UI Design Lead",
    email: "clara.vincy@email.com",
    phone: "+1 (555) 678-9012",
    location: "Portland, OR"
  },
  {
    fullName: "William Patrick",
    title: "Senior DevOps Engineer",
    email: "william.patrick@email.com",
    phone: "+1 (555) 789-0123",
    location: "Seattle, WA"
  },
  {
    fullName: "David Fernandes",
    title: "Full Stack Developer",
    email: "david.fernandes@email.com",
    phone: "+1 (555) 890-1234",
    location: "San Francisco, CA"
  }
];

/**
 * Get preview name data for template gallery
 */
const getFictionalCharacterData = (index) => {
  return PREVIEW_NAMES[index % PREVIEW_NAMES.length];
};

const TemplatesGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { parsedDataId, addonMode, filterTier: addonFilterTier, resumeData: addonResumeData } = location.state || {};
  
  const [templates, setTemplates] = useState([]);
  const [renderedTemplates, setRenderedTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [renderingProgress, setRenderingProgress] = useState({ done: 0, total: 0 });
  
  // Sort & Filter state — auto-set to silver if coming from addon modal
  const [sortOrder, setSortOrder] = useState('default'); // 'default' | 'silver-first' | 'platinum-first'
  const [filterTier, setFilterTier] = useState(addonMode ? 'silver' : 'all');   // 'all' | 'silver' | 'gold' | 'platinum'
  
  // Ref for render cancellation on unmount
  const renderAbortRef = useRef(false);

  // Tier priority map for sorting
  const TIER_PRIORITY = { silver: 1, gold: 2, platinum: 3 };

  // Apply filter and sort to templates
  const processedTemplates = React.useMemo(() => {
    let result = [...templates];
    
    // Apply filter
    if (filterTier !== 'all') {
      result = result.filter(t => (t.plan || '').toLowerCase() === filterTier);
    }
    
    // Apply sort
    if (sortOrder === 'silver-first') {
      result.sort((a, b) => (TIER_PRIORITY[a.plan?.toLowerCase()] || 0) - (TIER_PRIORITY[b.plan?.toLowerCase()] || 0));
    } else if (sortOrder === 'platinum-first') {
      result.sort((a, b) => (TIER_PRIORITY[b.plan?.toLowerCase()] || 0) - (TIER_PRIORITY[a.plan?.toLowerCase()] || 0));
    }
    
    return result;
  }, [templates, sortOrder, filterTier]);

  // Reset to page 1 when filter or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortOrder, filterTier]);

  // Calculate pagination values (based on processed templates)
  const totalPages = Math.ceil(processedTemplates.length / TEMPLATES_PER_PAGE);
  const startIdx = (currentPage - 1) * TEMPLATES_PER_PAGE;
  const visibleTemplates = processedTemplates.slice(startIdx, startIdx + TEMPLATES_PER_PAGE);

  // Wait for Firebase Auth to initialize, THEN fetch user plan
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userDocData = userDoc.data();
            setUserData(userDocData);
            const planData = {
              plan: userDocData.planType || 'free',
              isActive: userDocData.isActivePlan || false
            };
            setUserPlan(planData);
          } else {
            setUserPlan({ plan: 'free', isActive: false });
          }
        } else {
          setUserPlan({ plan: 'free', isActive: false });
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
        setUserPlan({ plan: 'free', isActive: false });
      } finally {
        setAuthResolved(true);
      }
    });

    return () => unsubscribe();
  }, []);

  // Show toast messages when returning from pricing page after successful payment
  useEffect(() => {
    if (location.state?.showSuccess && location.state?.message) {
      customToast.success(location.state.message, { autoClose: 8000 });
    }
    if (location.state?.draftSavedToMyFiles) {
      setTimeout(() => {
        customToast.info('📁 Your edited resume has been saved to the My Files section. You can access and download it from there anytime!', { autoClose: 10000 });
      }, 2500);
    }
    // Clean up location state to prevent re-triggering on page refresh
    if (location.state?.showSuccess || location.state?.draftSavedToMyFiles) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch templates and restore from IndexedDB cache
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        renderAbortRef.current = false;
        
        // Load templates metadata from Firebase Storage (just URLs, no HTML content)
        const templatesData = await loadTemplatesFromStorage();
        
        if (templatesData.length === 0) {
          setError('No templates found. Please ensure templates are uploaded to Firebase Storage.');
          return;
        }
        
        // Shuffle templates for mixed tier display
        const shuffled = shuffleArray(templatesData);
        setTemplates(shuffled);
        
        // Try to restore rendered HTML from IndexedDB cache
        const templateIds = shuffled.map(t => t.id);
        let cached = {};
        try {
          cached = await getCachedTemplates(templateIds);
        } catch (cacheErr) {
          // Cache unavailable, continue without it
        }
        const cachedCount = Object.keys(cached).length;
        
        if (cachedCount > 0) {
          setRenderedTemplates(cached);
        }
        
        // Identify templates that need rendering (not in cache AND not pre-rendered statically).
        // Templates with previewURL are served directly via <iframe src> — no runtime work needed.
        const uncachedTemplates = shuffled.filter(t => !cached[t.id] && !t.previewURL);
        
        if (uncachedTemplates.length > 0) {
          setRenderingProgress({ done: cachedCount, total: shuffled.length });
          // Start batch rendering (prioritizes current page)
          renderTemplatesInBatches(uncachedTemplates, shuffled, cached);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(`Failed to load templates: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
    
    return () => {
      renderAbortRef.current = true;
    };
  }, []);

  /**
   * Render templates in batches to avoid blocking the main thread.
   * Prioritizes templates visible on the current page.
   * Templates use pre-compiled Tailwind CSS (no JS compilation needed).
   */
  const renderTemplatesInBatches = async (uncachedTemplates, allTemplates, existingCache) => {
    let doneCount = Object.keys(existingCache).length;
    
    // Prioritize first page templates
    const firstPageIds = new Set(
      allTemplates.slice(0, TEMPLATES_PER_PAGE).map(t => t.id)
    );
    
    // Sort: current page first, then the rest
    const sorted = [
      ...uncachedTemplates.filter(t => firstPageIds.has(t.id)),
      ...uncachedTemplates.filter(t => !firstPageIds.has(t.id))
    ];

    for (let i = 0; i < sorted.length; i += RENDER_BATCH_SIZE) {
      if (renderAbortRef.current) break;
      
      const batch = sorted.slice(i, i + RENDER_BATCH_SIZE);
      
      // Render batch in parallel
      const results = await Promise.allSettled(
        batch.map((template) => renderSingleTemplate(template, allTemplates))
      );
      
      // Collect successful results
      const batchRendered = {};
      results.forEach((result, idx) => {
        const templateId = batch[idx].id;
        if (result.status === 'fulfilled' && result.value) {
          batchRendered[templateId] = result.value;
        } else {
          batchRendered[templateId] = null; // Mark as failed
        }
        doneCount++;
      });
      
      // Update state incrementally
      setRenderedTemplates(prev => ({ ...prev, ...batchRendered }));
      setRenderingProgress({ done: doneCount, total: allTemplates.length });
      
      // Cache the newly rendered batch in IndexedDB (with compiled CSS!)
      const successfulRenders = {};
      Object.entries(batchRendered).forEach(([id, html]) => {
        if (html) successfulRenders[id] = html;
      });
      if (Object.keys(successfulRenders).length > 0) {
        cacheRenderedTemplates(successfulRenders).catch(() => {});
      }
      
      // Yield to main thread between batches (prevents UI freeze)
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  };

  /**
   * Render a single template with sample data
   */
  const renderSingleTemplate = async (template, allTemplates) => {
    const templateIndex = allTemplates.findIndex(t => t.id === template.id);

    // Fetch template HTML from Firebase Storage
    const response = await fetch(template.downloadURL);
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }
    const templateHTML = await response.text();

    if (!templateHTML || templateHTML.length < 100) {
      throw new Error('Template HTML too short or empty');
    }

    // Sanitize and detect template fields
    const sanitizedHTML = sanitizeTemplateHTML(templateHTML);
    const tokens = extractTemplateTokens(sanitizedHTML);
    const allFields = Array.from(new Set([
      ...(tokens.variables || []),
      ...(tokens.sections || [])
    ]));

    // Get sample data with rotated preview names
    const baseSample = getSampleResumeData();
    const previewData = getFictionalCharacterData(templateIndex >= 0 ? templateIndex : 0);
    
    if (previewData) {
      baseSample.fullName = previewData.fullName;
      baseSample.title = previewData.title;
      baseSample.email = previewData.email;
      baseSample.phone = previewData.phone;
      baseSample.location = previewData.location;
    }
    
    // Build sample data with only the fields needed by this template
    const sampleData = {};
    for (const key of allFields) {
      if (baseSample[key] !== undefined) {
        sampleData[key] = baseSample[key];
      }
    }
    // Always include essential fields
    ['fullName', 'title', 'summary'].forEach(k => {
      if (baseSample[k] !== undefined) sampleData[k] = baseSample[k];
    });

    // Render with Mustache
    const renderedHTML = renderTemplate(sanitizedHTML, sampleData);
    
    if (!renderedHTML || renderedHTML.length < 100) {
      throw new Error('Rendered HTML too short');
    }
    
    return renderedHTML;
  };

  // Shuffle array helper
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Determine if user has access to a template
  const hasAccessToTemplate = (templatePlan) => {
    if (!userPlan) return true;
    if (!userPlan.isActive) return true;

    const userPlanType = userPlan.plan.toLowerCase();
    const templateTier = templatePlan.toLowerCase();

    const planHierarchy = { 'silver': 1, 'gold': 2, 'platinum': 3 };
    const userLevel = planHierarchy[userPlanType] || 1;
    const templateLevel = planHierarchy[templateTier] || 1;

    return userLevel >= templateLevel;
  };

  // Check if template should show upgrade button (locked)
  const isTemplateLocked = (templatePlan) => {
    if (!authResolved) return true;
    if (!userPlan) return false;
    if (!userPlan.isActive || userPlan.plan.toLowerCase() === 'free') return false;
    return !hasAccessToTemplate(templatePlan);
  };

  // Check if template should show badge
  const shouldShowBadge = (templatePlan) => {
    if (!userPlan) return true;
    if (!userPlan.isActive) return true;
    return !hasAccessToTemplate(templatePlan);
  };

  const handleTemplateSelect = (template) => {
    // AUTH CHECK: Ensure user is logged in
    const currentUser = auth.currentUser;
    if (!currentUser) {
      sessionStorage.setItem('pendingTemplateSelection', JSON.stringify({
        id: template.id,
        name: template.name,
        plan: template.plan,
        downloadURL: template.downloadURL
      }));
      
      navigate('/', {
        replace: true,
        state: {
          openLoginModal: true,
          message: 'Please log in to use templates'
        }
      });
      return;
    }

    // Check if template is locked (gold/platinum for silver user)
    if (isTemplateLocked(template.plan)) {
      if (userPlan && userPlan.plan.toLowerCase() === 'silver' && userData) {
        const resumeLimit = userData.resumeDownloadLimit ?? 0;
        const resumeUsed = userData.resumeDownloadsUsed ?? 0;
        const isUnlimited = resumeLimit === -1;

        if (!isUnlimited && resumeUsed >= resumeLimit) {
          // Silver user with exceeded limits trying to access gold/platinum → just toast
          customToast.error(`Download limit reached (${resumeUsed}/${resumeLimit}). Please upgrade your plan to access this template.`);
          return;
        }
      }
      navigate('/pricing');
      return;
    }

    // For Silver users who CAN access the template (silver template), but download limit exceeded
    // → proceed to editor anyway; the addon modal shows at download time in the editor
    // No blocking at gallery level for accessible templates

    // Navigate to editor — normalize template to include .tier from .plan for downstream code
    const navigationState = { selectedTemplate: { ...template, tier: template.plan || template.tier || 'silver' } };
    if (parsedDataId) {
      navigationState.parsedDataId = parsedDataId;
    }

    // If in addon mode, carry resume data so the editor pre-fills with existing content
    if (addonMode) {
      const savedResumeData = addonResumeData || JSON.parse(sessionStorage.getItem('addon_browse_resume_data') || 'null');
      if (savedResumeData) {
        navigationState.addonResumeData = savedResumeData;
      }
      // Clean up sessionStorage
      sessionStorage.removeItem('addon_browse_resume_data');
    }
    
    navigate('/resume-editor-v2', { state: navigationState });
  };

  // Pagination handlers
  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
    // Scroll to top of templates grid
    const container = document.querySelector('.templates-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate page numbers for pagination display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Loading state with attractive centered animation
  if (loading) {
    return (
      <div className="templates-loading-screen">
        <div className="loading-animation">
          <div className="spinner-container">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-logo">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <h2 className="loading-title">Loading Templates</h2>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="templates-gallery">
        <div className="templates-hero">
          <div className="hero-content">
            <h1 className="hero-title">Error Loading Templates</h1>
            <p className="hero-subtitle">{error}</p>
          </div>
        </div>
        <div className="error-container">
          <button 
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-gallery">
      {/* Hero Section */}
      <div className="templates-hero">
        <div className="hero-background">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-left">
            <h1 className="hero-title">
              Create a Resume That
              <span className="hero-title-highlight"> Represents You</span>
            </h1>
            
            <p className="hero-subtitle">
              Your resume is often the first impression you make. Our resume template library helps you present your skills and experience with clarity, confidence, and professionalism.
            </p>
          </div>
          
          <div className="hero-right">
            <div className="floating-cards">
              <div className="float-card card-1"></div>
              <div className="float-card card-2"></div>
              <div className="float-card card-3"></div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="hero-features-horizontal">
          <div className="hero-feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div className="feature-text">
              <strong>Exclusive Designs</strong>
              <span>In-house crafted templates with refined look and consistent structure</span>
            </div>
          </div>
          
          <div className="hero-feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="feature-text">
              <strong>Live Editing</strong>
              <span>Update instantly and see changes in real time - no formatting hassles</span>
            </div>
          </div>
          
          <div className="hero-feature-item">
            <div className="feature-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
                <polyline points="7.5 19.79 7.5 14.6 3 12"/>
                <polyline points="21 12 16.5 14.6 16.5 19.79"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div className="feature-text">
              <strong>Modern Standards</strong>
              <span>Clean, premium presentation for all roles, industries, and experience levels</span>
            </div>
          </div>
        </div>
      </div>

      {/* Resume Import Banner */}
      {parsedDataId && (
        <div className="import-success-banner" style={{
          margin: '2rem auto',
          maxWidth: '1200px',
          padding: '1.25rem 2rem',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div style={{ flex: 1, color: 'white' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
              Resume Imported Successfully!
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', opacity: 0.95 }}>
              Your resume data has been extracted. Select a template below to apply your information.
            </p>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      <div className="templates-container">
        {/* Template count, sort & filter controls */}
        <div className="templates-header-bar">
          <p className="templates-count">
            Showing {processedTemplates.length > 0 ? startIdx + 1 : 0}-{Math.min(startIdx + TEMPLATES_PER_PAGE, processedTemplates.length)} of {processedTemplates.length} templates
          </p>
          <div className="templates-controls">
            {renderingProgress.total > 0 && renderingProgress.done < renderingProgress.total && (
              <p className="rendering-progress">
                Preparing previews: {renderingProgress.done}/{renderingProgress.total}
              </p>
            )}
            <div className="control-group">
              <label className="control-label">Sort By</label>
              <select
                className="control-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="default">Default</option>
                <option value="silver-first">Silver → Gold → Platinum</option>
                <option value="platinum-first">Platinum → Gold → Silver</option>
              </select>
            </div>
            <div className="control-group">
              <label className="control-label">Filter By</label>
              <select
                className="control-select"
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
              >
                <option value="all">All Tiers</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
              </select>
            </div>
          </div>
        </div>

        <div className="templates-grid">
          {visibleTemplates.map((template) => {
            const locked = isTemplateLocked(template.plan);
            const hasRendered = !!renderedTemplates[template.id];
            const renderFailed = renderedTemplates[template.id] === null;
            
            return (
              <div 
                key={template.id} 
                className={`template-card ${locked ? 'locked' : ''}`}
                title={`Template: ${template.id}`}
              >
                <div className="template-preview-wrapper">
                  {/* Plan Badge */}
                  <div className={`plan-tier-badge ${template.plan.toLowerCase()}`}>
                    <span className="badge-icon">
                      {template.plan.toLowerCase() === 'silver' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      )}
                      {template.plan.toLowerCase() === 'gold' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" fill="currentColor"/>
                        </svg>
                      )}
                      {template.plan.toLowerCase() === 'platinum' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                          <circle cx="12" cy="8" r="1.5" fill="currentColor"/>
                          <circle cx="8" cy="10" r="1" fill="currentColor"/>
                          <circle cx="16" cy="10" r="1" fill="currentColor"/>
                        </svg>
                      )}
                    </span>
                    <span className="badge-label">{template.plan.charAt(0).toUpperCase() + template.plan.slice(1)}</span>
                  </div>
                  
                  {/* Template Preview
                       Priority 1: pre-rendered static HTML served via <iframe src> (works identically on localhost & Firebase)
                       Priority 2 (fallback): runtime srcDoc rendering (legacy path)
                       Priority 3: skeleton / error placeholder */}
                  {template.previewURL ? (
                    <LazyTemplateCard
                      iframeSrc={template.previewURL}
                      templateName={template.name}
                    />
                  ) : hasRendered ? (
                    <LazyTemplateCard 
                      renderedHTML={renderedTemplates[template.id]}
                      templateName={template.name}
                    />
                  ) : renderFailed ? (
                    <div className="template-preview-error">
                      <div className="error-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      </div>
                      <p>Preview unavailable</p>
                      <small>Template will work in editor</small>
                    </div>
                  ) : (
                    <TemplateSkeletonCard />
                  )}
                  
                  {/* Overlay with action button */}
                  <div className="template-overlay">
                    <button 
                      className={`use-template-btn ${locked ? 'locked-btn' : ''}`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {locked ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{marginRight: '8px'}}>
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="white"/>
                          </svg>
                          Upgrade to Use
                        </>
                      ) : (
                        'Use Template'
                      )}
                    </button>
                  </div>
                </div>
                <div className="template-info">
                  <h3 className="template-name">{template.name}</h3>
                  <span className={`plan-badge ${template.plan}`}>
                    {template.plan}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="templates-pagination">
            <button 
              className="pagination-btn pagination-prev"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Previous
            </button>
            
            <div className="pagination-pages">
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`dots-${idx}`} className="pagination-dots">...</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>
            
            <button 
              className="pagination-btn pagination-next"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Addon Pricing Modal */}
      <AddonPricingModal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
      />
    </div>
  );
};

export default TemplatesGallery;

